import { App, Button, Form, Input, Modal } from 'antd';
import { useEffect, useRef, useState } from 'react';
import usersApiService from 'src/api/services/usersApiService';
import { UserInfo } from 'src/common/commonTypes';
import UserIcon from './userIcon';

type Props = {
    userInfo: UserInfo;
};

type FormFields = {
    authorizationNumber: string;
};

function AuthorizationNumberEditor(props: Props) {
    const { notification } = App.useApp();
    const [form] = Form.useForm<FormFields>();
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [prevAuthorizationNumber, setPrevAuthorizationNumber] = useState<string | undefined>(undefined);
    const cancelled = useRef(false);

    useEffect(() => {
        return () => {
            cancelled.current = true;
        };
    }, []);

    const handleButtonClick = async () => {
        if (isLoading) {
            return;
        }
        setIsOpen(true);
        setIsLoading(true);
        try {
            const user = await usersApiService.getUser(props.userInfo.userId);
            setIsLoading(false);
            if (user.authorizationNumber && !cancelled.current) {
                form.setFieldsValue({
                    authorizationNumber: user.authorizationNumber
                });
                setPrevAuthorizationNumber(user.authorizationNumber);
            }
        } catch (error) {
            setIsLoading(false);
            if (error instanceof Error) {
                notification.error({ message: error.message });
            }
            setIsOpen(false);
        }
    };

    const handleSaveClick = async () => {
        if (isProcessing) {
            return;
        }
        setIsProcessing(true);
        const authorizationNumber = form.getFieldValue('authorizationNumber');
        try {
            await usersApiService.setAuthorizationNumber({
                id: props.userInfo.userId,
                authorizationNumber
            });
            setPrevAuthorizationNumber(undefined);
            setIsProcessing(false);
            setIsOpen(false);
        } catch (error) {
            setIsProcessing(false);
            if (error instanceof Error) {
                notification.error({ message: error.message });
            }
            return;
        }

        window.location.reload();
    };

    const handleCancelClick = () => {
        if (isProcessing) {
            return;
        }
        setPrevAuthorizationNumber(undefined);
        setIsOpen(false);
    };

    return (
        <>
            <Button className="user-name-button" onClick={handleButtonClick} type="link">
                <UserIcon />
                <span>{props.userInfo.userName}</span>
            </Button>
            <Modal open={isOpen} title="Данные МЧД" onCancel={handleCancelClick} footer={null} destroyOnClose>
                <Form className="modal-form" form={form} layout="vertical" preserve={false}>
                    <Form.Item name="authorizationNumber" label="Номер МЧД">
                        <Input placeholder="Введите" disabled={isLoading || isProcessing} />
                    </Form.Item>
                    <Form.Item shouldUpdate noStyle>
                        {({ getFieldValue }) => {
                            const authorizationNumber: string = getFieldValue('authorizationNumber');
                            const changed = prevAuthorizationNumber !== authorizationNumber;
                            return (
                                <div className="modal-form-actions">
                                    <Button
                                        onClick={handleSaveClick}
                                        type="primary"
                                        disabled={isLoading || !changed}
                                        loading={isProcessing}
                                    >
                                        Сохранить
                                    </Button>
                                    <Button onClick={handleCancelClick} disabled={isProcessing}>
                                        Отмена
                                    </Button>
                                </div>
                            );
                        }}
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}

export default AuthorizationNumberEditor;
